import moment from 'moment-timezone';
import { defaultTo } from 'lodash';
import { INTERRUPT_CHECK_DAYS } from './constants';

// Helper to execute date and time related operations for content from graphql-server

const MOMENT_TZ_TORONTO = 'America/Toronto';
const MOMENT_DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss';
const MOMENT_DATE_MIN = '1970-01-01T00:00:00';
const MOMENT_DATE_MAX = '2099-12-31T23:59:59';

export const filterOutNull = <T>(arr: (T | null | undefined)[] | null | undefined): T[] => {
  if (arr && arr.constructor === Array) {
    return arr.filter((item) => item !== null && item !== undefined);
  }
  return [];
};

export const isNotExpired = <T extends object>(item: T, startDateField: keyof T, endDateField: keyof T) => {
  const startDate = item[startDateField];
  const endDate = item[endDateField];

  if (!startDate || !endDate) {
    return false;
  }

  const currentMoment = moment().tz(MOMENT_TZ_TORONTO);
  const startMoment = moment.tz(
    String(defaultTo(startDate, MOMENT_DATE_MIN)),
    MOMENT_DATETIME_FORMAT,
    MOMENT_TZ_TORONTO,
  );
  const endMoment = moment.tz(
    String(defaultTo(endDate, MOMENT_DATE_MAX)),
    MOMENT_DATETIME_FORMAT,
    MOMENT_TZ_TORONTO,
  );

  return (
    currentMoment.isSameOrAfter(startMoment)
    && currentMoment.isSameOrBefore(endMoment)
  );
};

export const filterOutExpired = <T extends object>(
  arr: T[] | null | undefined, startDate: keyof T, endDate: keyof T,
): T[] => {
  if (arr && arr.constructor === Array) {
    return arr.filter((item) => isNotExpired(item, startDate, endDate));
  }
  return [];
};

export const getExpiryTimeout = (endDate: string | null | undefined) => {
  const currentMoment = moment().tz(MOMENT_TZ_TORONTO);
  const expiryMoment = moment.tz(
    endDate || MOMENT_DATE_MAX,
    MOMENT_DATETIME_FORMAT,
    MOMENT_TZ_TORONTO,
  );
  return Math.min(expiryMoment.diff(currentMoment), 2147483646);
};

type InterruptUserData = {
  interruptShops?: { id?: string | null; dateAdded?: string | null }[] | null;
};

export const getSavedInterruptShop = (shopId: string | null | undefined, userData: InterruptUserData) => (
  shopId ? userData.interruptShops?.find((shop) => shop.id === shopId) : undefined
);

export const showInterruptMessage = (props: {
  shopData: { interstitialInterrupt?: boolean | null; id?: string | null; lastUpdated?: string | null };
  userData: InterruptUserData;
}) => {
  const {
    shopData: { interstitialInterrupt, id: shopId, lastUpdated },
    userData,
  } = props;

  if (!interstitialInterrupt) {
    return false;
  }

  const interruptShop = getSavedInterruptShop(shopId, userData);
  if (!interruptShop || !lastUpdated || !interruptShop.dateAdded) {
    return true;
  }

  const updatedAt = moment(lastUpdated);
  const acknowledgedAt = moment(interruptShop.dateAdded);
  return !updatedAt.isValid()
    || !acknowledgedAt.isValid()
    || updatedAt.isAfter(acknowledgedAt)
    || moment().diff(acknowledgedAt, 'days', true) >= INTERRUPT_CHECK_DAYS;
};

export const getInitialInterruptState = (props: Parameters<typeof showInterruptMessage>[0]) => ({
  showInterruptMessage: showInterruptMessage(props),
  hasSavedInterruptPreference: Boolean(getSavedInterruptShop(props.shopData.id, props.userData)),
});
